import type { CanonicalMembershipType } from "@dj/convex-api";

// Geometry and palettes come from the owner-supplied SVGs in ../assets/memberships.
// Widths include the outer stroke. The rotated Pro rectangle includes its rounded corners.
const artworkWidths: Readonly<Record<CanonicalMembershipType, number>> = {
  community: 2 * 10.6 + 1.4,
  pilot_operator: 20.6 + 1.4,
  pilot_professional: (15 - 2 * 3.2) * Math.SQRT2 + 2 * 3.2 + 1.4,
  drone_business: 21.1 - 2.9 + 1.4,
  enterprise: 22.3 - 1.7 + 1.4,
};

const palettes: Readonly<
  Record<CanonicalMembershipType, readonly [string, string]>
> = {
  community: ["#C3834D", "#99592B"],
  pilot_operator: ["#8E99A6", "#626E7B"],
  pilot_professional: ["#F6CB35", "#D9A80E"],
  drone_business: ["#D63753", "#A3152F"],
  enterprise: ["#9255D6", "#5C219E"],
};

function CrestMotif({
  membershipType,
  gradientId,
}: Readonly<{
  membershipType: CanonicalMembershipType;
  gradientId: string;
}>) {
  switch (membershipType) {
    case "community":
      return (
        <>
          <circle
            cx="12"
            cy="12"
            r="10.6"
            fill={`url(#${gradientId})`}
            stroke="#6E3D1B"
            strokeWidth="1.4"
          />
          <g fill="#FFF">
            <ellipse cx="12" cy="7.5" rx="2.5" ry="3.8" />
            <ellipse
              cx="12"
              cy="7.5"
              rx="2.5"
              ry="3.8"
              transform="rotate(120 12 12)"
            />
            <ellipse
              cx="12"
              cy="7.5"
              rx="2.5"
              ry="3.8"
              transform="rotate(240 12 12)"
            />
            <circle cx="12" cy="12" r="2.4" />
          </g>
          <circle cx="12" cy="12" r="0.9" fill="#99592B" />
        </>
      );
    case "pilot_operator":
      return (
        <>
          <rect
            x="1.7"
            y="1.7"
            width="20.6"
            height="20.6"
            rx="5.2"
            fill={`url(#${gradientId})`}
            stroke="#48525E"
            strokeWidth="1.4"
          />
          <path
            d="M9.97 8.37Q12 10.5 14.03 8.37L15.63 9.97Q13.5 12 15.63 14.03L14.03 15.63Q12 13.5 9.97 15.63L8.37 14.03Q10.5 12 8.37 9.97Z"
            fill="#FFF"
            stroke="#FFF"
            strokeWidth="0.6"
            strokeLinejoin="round"
          />
          <g fill="none" stroke="#FFF" strokeWidth="1.5">
            <circle cx="7.6" cy="7.6" r="2.6" />
            <circle cx="16.4" cy="7.6" r="2.6" />
            <circle cx="7.6" cy="16.4" r="2.6" />
            <circle cx="16.4" cy="16.4" r="2.6" />
          </g>
        </>
      );
    case "pilot_professional":
      return (
        <>
          <rect
            x="4.5"
            y="4.5"
            width="15"
            height="15"
            rx="3.2"
            transform="rotate(45 12 12)"
            fill={`url(#${gradientId})`}
            stroke="#9A7406"
            strokeWidth="1.4"
          />
          <path
            d="M12 6.6 13.65 10.13 17.52 10.61 14.66 13.27 15.41 17.09 12 15.2 8.59 17.09 9.34 13.27 6.48 10.61 10.35 10.13Z"
            fill="#553C00"
            stroke="#553C00"
            strokeWidth="0.9"
            strokeLinejoin="round"
          />
        </>
      );
    case "drone_business":
      return (
        <>
          <path
            d="M12 1.7 21.1 6.9V17.1L12 22.3 2.9 17.1V6.9Z"
            fill={`url(#${gradientId})`}
            stroke="#760C22"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path
            d="M12 7.3 7.4 15.2H16.6Z"
            fill="none"
            stroke="#FFF"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <g fill="#FFF">
            <circle cx="12" cy="7.3" r="2.7" />
            <circle cx="7.4" cy="15.2" r="2.7" />
            <circle cx="16.6" cy="15.2" r="2.7" />
          </g>
        </>
      );
    case "enterprise":
      return (
        <>
          <path
            d="M8.1 1.7H15.9L22.3 8.1V15.9L15.9 22.3H8.1L1.7 15.9V8.1Z"
            fill={`url(#${gradientId})`}
            stroke="#3F136F"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <g fill="none" stroke="#FFF" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="6.6" />
            <ellipse cx="12" cy="12" rx="2.8" ry="6.6" />
            <path d="M5.4 12H18.6M6.6 8.4H17.4M6.6 15.6H17.4" />
          </g>
        </>
      );
  }
}

export function MembershipCrestArtwork({
  membershipType,
  gradientId,
}: Readonly<{
  membershipType: CanonicalMembershipType;
  gradientId: string;
}>) {
  const [top, bottom] = palettes[membershipType];
  // All five occupy 54 units of the 64-unit frame in width. The taller Business
  // hexagon still fits vertically; proportional scaling preserves every motif.
  const scale = 54 / artworkWidths[membershipType];
  return (
    <>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={top} />
          <stop offset="1" stopColor={bottom} />
        </linearGradient>
      </defs>
      <g transform={`translate(32 32) scale(${scale}) translate(-12 -12)`}>
        <CrestMotif membershipType={membershipType} gradientId={gradientId} />
      </g>
    </>
  );
}
